import { useEffect, useState } from "react";
import { Row, Space, Typography, Alert } from "antd";
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"

import Transactions from "../transactions/index";
import { backendGetRequest, backendGetRequestWithCache, backendPatchWithoutRefresh } from "../../../helpers/callBackend";
import BalanceInfo from "./balanceInfo";
import ChangeInCashInfo from "./changeInCashInfo";
import ActionInfo from "./actionInfo";
import TitledSection from "../../generic/titledSection";
import moment from "moment";
import FormattedTabs from "../../generic/formattedTabs";
import { useUcs } from "../../../helpers/contexts/UserCompanySettingsContext";
import SftpFileCopies from "../singleBankAccountTables/sftpFileCopies";
import SftpPositions from "../singleBankAccountTables/SftpPositions";

const { Text } = Typography;

function extractTimeZone(datetimeString) {
	let date = new Date(datetimeString);
	let matchArr = date.toString().match(/\((.*)\)/);
	let timezone = "";
	if (matchArr.length >= 2) {
		timezone = matchArr[1];
	}
	return timezone;
}

const OpenedBankAccount = (props) => {
	const [totalRecordsTransactions, setTotalRecordsTransactions] = useState(0);
	const [allRecordsReceivedTransactions, setAllRecordsReceivedTransactions] = useState(false);
	const [loadingIncompleteTransactions, setLoadingIncompleteTransactions] = useState(true);
	const [loadingAchDebitRequests, setLoadingAchDebitRequests] = useState(true);
	const [transactionsTable, setTransactionsTable] = useState([]);
	const [completedTransactions, setCompletedTransactions] = useState([]);
	const [pendingTable, setPendingTable] = useState([]);
	const [scheduledTable, setScheduledTable] = useState([]);
	const [canceledTable, setCanceledTable] = useState([]);
	const [achDebitRequestsTable, setAchDebitRequestsTable] = useState([]);
	const [last30daysTxnsTotal, setLast30daysTxnsTotal] = useState(0);
	const [last1dayTxnsTotal, setLast1dayTxnsTotal] = useState(0);

	const [availableBalance, setAvailableBalance] = useState(localStorage.getItem("account_balance") || undefined);
	const [currentBalance, setCurrentBalance] = useState(localStorage.getItem("account_balance") || undefined);
	const [ICSBalance, setICSBalance] = useState(undefined)
	const [aggregatedBalance, setAggregatedBalance] = useState(undefined)
	const [currency, setCurrency] = useState("USD");
	const [currencySymbol, setCurrencySymbol] = useState("$");
	const [lastBalanceRefreshComponent, setLastBalanceRefreshComponent] = useState(<></>);

	const [transactionsLoading, setTransactionsLoading] = useState(true);
	const [transactionsLoadedTimes, setTransactionsLoadedTimes] = useState(0);
	const AUTO_TXNS_LOAD_LIMIT = 6;

	const todayDate = new Date();
	let [currentMonth, setCurrentMonth] = useState(todayDate.getMonth() + 1);
	let [currentYear, setCurrentYear] = useState(todayDate.getFullYear());

	const [hasNewTransactions, setHasNewTransactions] = useState(false);
	const [paymentRequests, setPaymentRequests] = useState([])
	const [loadingPaymentRequests, setLoadingPaymentRequests] = useState(false)
	const [doneLoadingPrs, setDoneLoadingPrs] = useState(false)
	const [totalPrs, setTotalPrs] = useState(0)
	const [userHasRestrictedAcces, setUserHasRestrictedAccess] = useState(true)
	const [positionsData, setPositionsData] = useState([])
	const [sftpTabs, setSftpTabs] = useState([])

	const ucsContext = useUcs()
	useEffect(() => {
		if (ucsContext.loaded) {
			setUserHasRestrictedAccess(ucsContext.hasLimitedAccountAccess())
		}
	}, [ucsContext])

	useEffect(() => {
		if (props?.bankAccountInfo?.source === 'FT' && !userHasRestrictedAcces) {
			getAccountPositions()
			setSftpTabs((prevTabs) => {
				return [
					...prevTabs,
					{
						title: 'Files',
						key: 1,
						body: <SftpFileCopies bankAccountId={props.bankAccountId} />,
					},
				]
			})
		}
	}, [props?.bankAccountInfo?.source, userHasRestrictedAcces])

	useEffect(() => {
		if (positionsData.length > 0) {
			setSftpTabs((prevTabs) => {
				return [
					...prevTabs,

					{
						title: 'Positions',
						key: 2,
						body: <SftpPositions positionsData={positionsData} />,
					},
				]
			})
		}
	}, [positionsData])


	useEffect(() => {
		setAvailableBalance(props.bankAccountInfo?.available_balance_raw_base_currency)
		setCurrentBalance(props.bankAccountInfo?.current_balance_raw_base_currency)
		setICSBalance(props.bankAccountInfo?.ics_balance_raw_base_currency)
		setAggregatedBalance(props.bankAccountInfo?.aggregated_balance_raw_base_currency)
		setCurrency(props.bankAccountInfo?.currency || "USD")
		setCurrencySymbol(props.bankAccountInfo?.currency_symbol || "$")
		let lastCurrentBalanceFetch = props.bankAccountInfo?.last_current_balance_fetch
		if (lastCurrentBalanceFetch !== undefined) {
			const refreshDate = moment(lastCurrentBalanceFetch).format("YYYY-MM-DD");
			const refreshTime = moment(lastCurrentBalanceFetch).format("hh:mm A");
			const refreshTimeZone = extractTimeZone(lastCurrentBalanceFetch)
			setLastBalanceRefreshComponent(
				<Text disabled italic>
					{`Balance last updated on ${refreshDate} at ${refreshTime} (${refreshTimeZone})`}
				</Text>
			)
		}
	}, [props.bankAccountInfo])

	const getAccountPositions = async () => {
		try {
			const posResp = await backendGetRequest('/api/sftp/account-positions', {
				params: { bank_account_id: props.bankAccountId }
			});
			setPositionsData(posResp.data?.data || []);
		} catch (error) {
			setPositionsData([]);
		}
	}

	const refreshTransactions = async () => {
		const refreshResp = await backendPatchWithoutRefresh(
			"/api/transactions/refresh/",
			{
				bank_account_id: props.bankAccountId,
				txns: completedTransactions.slice(0, 100),
			}
		)
		if (refreshResp.status == 200) {
			if (refreshResp.data.data.txns) {
				let newCompletedTransactions = completedTransactions;
				refreshResp.data.data.txns.forEach((transaction) => {
					const existingTransactionIndex = newCompletedTransactions.findIndex(txn => txn.id === transaction.id);
					if (existingTransactionIndex !== -1) {
						newCompletedTransactions[existingTransactionIndex] = transaction;
					} else {
						newCompletedTransactions.push(transaction);
					}
				});
				// setCompletedTransactions(newCompletedTransactions);
			}
		}
	};

	const updatePaymentRequests = (paymentRequest, method) => {
		// edit payment requests data displayed to user
		if (!paymentRequest) {
			return
		}
		let newPaymentReqs = [...paymentRequests]
		switch (method) {
			case "add":
				newPaymentReqs.push(paymentRequest)
				break;
			case "replace":
				newPaymentReqs = paymentRequests.map((currentPr) => {
					if (currentPr.id === paymentRequest.id) {
						return paymentRequest
					}
					return currentPr
				})
				break;
			case "remove":
				newPaymentReqs = paymentRequests.filter(currentPr => currentPr.id !== paymentRequest.id)
				break;
		}
		setPaymentRequests(newPaymentReqs)
	}

	useEffect(() => {
		if (completedTransactions.length >= totalRecordsTransactions) {
			setAllRecordsReceivedTransactions(true);
		}
	}, [completedTransactions]);

	useEffect(() => {
		if (allRecordsReceivedTransactions && hasNewTransactions) {
			refreshTransactions();
		}
	}, [allRecordsReceivedTransactions, hasNewTransactions]);

	useEffect(() => {
		if (!transactionsLoading && transactionsLoadedTimes < AUTO_TXNS_LOAD_LIMIT && completedTransactions.length < 20) {
			onLoadTransactions({ month: currentMonth, year: currentYear });
		}
	}, [transactionsLoading]);

	const cancelAchDebitRequest = (achDebitRequestId) => {
		setAchDebitRequestsTable((prevState) => {
			return prevState.filter((achDebitRequest) => {
				return achDebitRequest.id !== achDebitRequestId;
			});
		});
	};

	const addAchDebitRequest = (newAchDebitRequest) => {
		setAchDebitRequestsTable((prevState) => {
			return [newAchDebitRequest, ...prevState];
		});
	};

	const updateInternalTransactions = (newTransaction) => {
		setTransactionsTable((prevState) => {
			return [newTransaction, ...prevState];
		});
		props.sendingFunds(newTransaction);
	};

	const updateCheckDeposits = (newCheckDeposit) => {
		setPendingTable((prevState) => {
			return [newCheckDeposit, ...prevState];
		});
	};

	const updateExternalTransactions = (newTransaction, formInput) => {
		newTransaction.attributes.amount /= 100;
		newTransaction.attributes.amount *= -1;
		if (newTransaction.type === "Originated Wire" || newTransaction.attributes.schedule === undefined) {
			setPendingTable((prevState) => {
				return [newTransaction, ...prevState];
			});
		} else {
			setScheduledTable((prevState) => {
				return [newTransaction, ...prevState];
			});
		}
	};

	const caculateChangeInCash = (txns_data, time_period) => {
		let today = new Date();
		let start_date = new Date(new Date().setDate(today.getDate() - time_period)).toISOString().split("T")[0];
		let end_date = new Date().toISOString().split("T")[0];
		let data = txns_data;
		let value = 0;
		let already_calculated = {};
		for (let i = 0; i < data.length; i++) {
			if (already_calculated[data[i].id] !== undefined) {
				continue;
			}
			let txn_date = data[i].timestamp.split("T")[0];
			if (txn_date >= start_date && txn_date <= end_date) {
				value += parseFloat(data[i]["amount"]);
				already_calculated[data[i].id] = true;
			}
		}
		return value;
	};

	const decrementMonth = (month, year, decrementInplace = false) => {
		if (month === 1) {
			month = 12;
			year -= 1;
		} else {
			month -= 1;
		}
		if (decrementInplace) {
			setCurrentMonth(month);
			setCurrentYear(year);
		}
		return { month: month, year: year };
	}

	const onLoadTransactions = async ({ month, year }) => {
		setTransactionsLoadedTimes(prevState => prevState + 1);
		setTransactionsLoading(true);
		decrementMonth(month, year, true);
		backendGetRequest(
			"/api/transactions/",
			{
				params: {
					bank_account_id: props.bankAccountId,
					month: month,
					year: year,
					page_index: 0,
					includeCompleteTransactions: true,
				},
			},
			""
		).then((res) => {
			const d = res?.data;
			setTotalRecordsTransactions(res?.data?.total_records);
			setHasNewTransactions(res?.data?.new_transactions > 0);
			let requestPromises = [];
			for (let i = 1; i <= d?.total_pages; i++) {
				const requestPromise = backendGetRequestWithCache(
					"/api/transactions/",
					{
						params: {
							bank_account_id: props.bankAccountId,
							page_index: i,
							month: month,
							year: year,
							includeCompleteTransactions: true,
						},
					},
					(cachedResponse) => {
						if (cachedResponse?.data) {
							let newCompletedTransactions = cachedResponse?.data?.bank_account?.transactions?.filter((transaction) => {
								return transaction.is_complete;
							});
							setCompletedTransactions((prevState) => {
								const filteredTransactions = newCompletedTransactions.filter(
									(newTransaction) => !prevState.some((prevTransaction) => prevTransaction.id === newTransaction.id)
								);
								return [...prevState, ...filteredTransactions];
							});
						}
					},
					(nonCachedResponse) => {
						if (nonCachedResponse?.data) {
							let newCompletedTransactions = nonCachedResponse?.data?.bank_account?.transactions?.filter((transaction) => {
								return transaction.is_complete;
							});
							setCompletedTransactions(prevState => {
								let newTransactions = [...prevState];
								newCompletedTransactions.forEach(transaction => {
									const existingTransactionIndex = newTransactions.findIndex(txn => txn.id === transaction.id);
									if (existingTransactionIndex !== -1) {
										newTransactions[existingTransactionIndex] = transaction;
									} else {
										newTransactions.push(transaction);
									}
								});
								return newTransactions;
							});
						}
					}
				);
				requestPromises.push(requestPromise);
			}
			return Promise.all(requestPromises).then(() => {
				setTransactionsLoading(false);
			});
		});
	}

	const getPrPage = async (pageNum) => {
		try {
			const prResp = await backendGetRequest('/api/payments/payment-request', {
				params: {
					bank_account_id: props.bankAccountId,
					filter: 'from',
					page_index: pageNum,
				},
			})
			const paymentRequestArray = prResp.data?.data
			setPaymentRequests((prevState) => [...prevState, ...paymentRequestArray])
		} catch (error) {
			// error getting payment request objects
			return
		}
	}

	const getPaymentRequests = async () => {
		setPaymentRequests([])
		let totalPages = 0
		try {
			const paginationResp = await backendGetRequest('/api/payments/payment-request', {
				params: {
					bank_account_id: props.bankAccountId,
					filter: 'from',
					page_index: 0,
				},
			})
			totalPages = paginationResp.data?.total_pages
			setTotalPrs(paginationResp.data?.total_records)
		} catch (error) {
			// error while getting pagination info
			return
		}

		for (let pageNum = 1; pageNum < totalPages + 1; pageNum++) {
			getPrPage(pageNum)
		}
	}

	useEffect(() => {
		// checks if we have pulled all prs yet
		if (doneLoadingPrs) {
			return
		}
		if (totalPrs > 0 && paymentRequests.length >= totalPrs) {
			setLoadingPaymentRequests(false)
			setDoneLoadingPrs(true)
		} else {
			setLoadingPaymentRequests(true)
		}
	}, [paymentRequests])

	const callInitialTransactions = async () => {
		await onLoadTransactions({ month: currentMonth, year: currentYear })
		await onLoadTransactions(decrementMonth(currentMonth, currentYear));
		await getPaymentRequests()
	}

	useEffect(() => {
		if (props.bankAccountId !== undefined) {
			callInitialTransactions();

			backendGetRequest(
				"/api/transactions/",
				{
					params: {
						bank_account_id: props.bankAccountId,
						includeCompleteTransactions: false,
						includeIncompleteTransactions: true,
					},
				},
				"loadingIncompleteTransactions"
			).then((res) => {
				const d = res?.data;
				setLoadingIncompleteTransactions(false);
				setPendingTable(d?.bank_account?.pending || []);
				setScheduledTable(d?.bank_account?.scheduled || []);
				setCanceledTable(d?.bank_account?.canceled || []);
			});

			if (props.bankAccountInfo?.has_ach_debit) {

				backendGetRequest(
					"/api/payments/ach-debit/",
					{
						params: {
							bank_account_id: props.bankAccountId,
							page_index: 0,
						},
					},
					""
				).then((res) => {
					const d = res?.data;
					for (let i = 1; i <= d?.total_pages; i++) {
						backendGetRequest(
							"/api/payments/ach-debit/",
							{
								params: {
									bank_account_id: props.bankAccountId,
									page_index: i,
								},
							},
							"loadingAchDebitRequests"
						).then((res) => {
							const d = res?.data;
							setLoadingAchDebitRequests(false);
							setAchDebitRequestsTable((prevState) => {
								return [...prevState, ...d?.ach_debit_requests];
							});
						});
					}
				});
			} else {
				setLoadingAchDebitRequests(false);
			}
		}
	}, [props.bankAccountId]);

	const updateTransactionsTable = () => {
		if (pendingTable === undefined) return
		setTransactionsTable((prevState) => {
			return completedTransactions.filter((transaction) => {
				return !pendingTable.some((pendingTransaction) => "unit_" + pendingTransaction.attributes.transaction?.data?.id === transaction.id);
			});
		});
	}

	useEffect(() => {
		updateTransactionsTable();
	}, [pendingTable, completedTransactions]);

	useEffect(() => {
		setLast30daysTxnsTotal(caculateChangeInCash([...transactionsTable], 30));
		setLast1dayTxnsTotal(caculateChangeInCash([...transactionsTable], 1));
	}, [transactionsTable]);

	/**
	 * Update table data using an array of {id: str, balance: number } objects with new balances.
	 * 
	 * @param {Object[]} recalData object response from a backend recalibrate request
	 * 
	 */
	const onRecalibrateTransactions = (recalData) => {
		setTransactionsTable((prevState) => {
			const newState = prevState.map((row) => {
				const updatedData = recalData.find(item => item.id === row.id)
				if (updatedData) {
					return { ...row, balance: updatedData.balance }
				}
				return row
			})
			return newState
		})
	}

	return (
		<Space direction="vertical" style={{ width: "100%" }}>

			{props.bankAccountInfo !== undefined && !props.bankAccountInfo.api_connection_status_live && (
				<Alert
					message="This account is unlinked. Please reconnect your account for live balances and transactions!"
					banner
				/>
			)}

			{(props.bankAccountInfo !== undefined && (props.bankAccountInfo.status === "Closed" || props.bankAccountInfo.marked_closed)) && (
				<Alert
					message="This account is closed!"
					banner
				// closable
				/>
			)}

			{props.bankAccountInfo !== undefined && props.bankAccountInfo.source === "MA" && (
				<Alert
					message="This account is maintained manually. The feed is not live!"
					banner
				/>
			)}

			<TitledSection
				title={""}
				body={
					<Space direction="vertical" style={{ width: "100%" }}>

						<div >
							<Box display="flex" flexDirection="row" gap={2}>
								<Box key={"1"} display="flex" flex={1} gap={2}>
									{/* Left Section - BalanceInfo (With ActionInfo Below) */}
									<Box display="flex" flexDirection="column" flex={1} gap={2}>
										<Paper elevation={0} sx={{ flex: 1 }}>
											<BalanceInfo
												setBankAccountData={props.setBankAccountData}
												allowBalanceEdit={props.bankAccountInfo?.source === "MA"}
												bankInfo={props.bankAccountInfo}
												bankAccountId={props.bankAccountId}
												genericTags={props.genericTags}
												purpose={props.purpose}
												availableBalance={availableBalance}
												currentBalance={currentBalance}
												ICSBalance={ICSBalance}
												aggregatedBalance={aggregatedBalance}
												currency={currency}
												currencySymbol={currencySymbol}
												lastBalanceRefreshComponent={lastBalanceRefreshComponent}
											/>
										</Paper>
										<Paper elevation={0} sx={{ flex: 1 }}>
											<ActionInfo
												bankAccountInfo={props.bankAccountInfo}
												bankAccountId={props.bankAccountId}
												loading={props.loading}
												beneficiaryInfo={props.beneficiaryInfo}
												updateInternalTransactions={updateInternalTransactions}
												updateExternalTransactions={updateExternalTransactions}
												setTransactionsTable={setTransactionsTable}
												addAchDebitRequest={addAchDebitRequest}
												updateCheckDeposits={updateCheckDeposits}
												paymentRequests={paymentRequests}
												updatePaymentRequests={updatePaymentRequests}
												setBankAccountData={props.setBankAccountData}
											/>
										</Paper>
									</Box>
									<Paper elevation={0} sx={{ flex: 1 }}>
										<ChangeInCashInfo
											loadingTransactions={!allRecordsReceivedTransactions}
											last30daysTxnsTotal={last30daysTxnsTotal}
											last1dayTxnsTotal={last1dayTxnsTotal}
											completedTransactions={completedTransactions}
										/>
									</Paper>
								</Box>
							</Box>
						</div>
						{props.bankAccountInfo && props.bankAccountInfo.daca_lender !== null && (
							<Row>
								<Text italic={true} type="secondary">
									*This is a DACA account, the lender is {props.bankAccountInfo.daca_lender}.
								</Text>
							</Row>
						)}

						{props.bankAccountInfo?.source === 'FT' && !userHasRestrictedAcces ? (
							<FormattedTabs
								defaultActiveKey={0}
								tabPanes={[
									{
										title: 'Transactions',
										key: 0,
										body: (
											<Transactions
												bankAccountId={props.bankAccountId}
												bankAccountName={
													props.bankAccountInfo !== undefined
														? props.bankAccountInfo.account_description
														: localStorage.getItem('account_name')
												}
												bankAccountDescription={
													props.bankAccountInfo !== undefined
														? props.bankAccountInfo.bank_account_name
														: localStorage.getItem('account_name')
												}
												onLoadTransactions={onLoadTransactions}
												transactionsLoading={transactionsLoading}
												currentMonth={currentMonth}
												currentYear={currentYear}
												transactionsTable={transactionsTable}
												pendingTable={pendingTable}
												scheduledTable={scheduledTable}
												canceledTable={canceledTable}
												achDebitRequestsTable={achDebitRequestsTable}
												loadingTransactions={transactionsLoading}
												loadingIncompleteTransactions={loadingIncompleteTransactions}
												loadingAchDebitRequests={loadingAchDebitRequests}
												completeTransactionsNumerator={completedTransactions.length}
												completeTransactionsDenominator={totalRecordsTransactions}
												cancelAchDebitRequest={cancelAchDebitRequest}
												portfolioName={props.bankAccountInfo ? props.bankAccountInfo.subportfolio : undefined}
												propertyName={props.bankAccountInfo ? props.bankAccountInfo.property : undefined}
												apiSource={props.bankAccountInfo !== undefined ? props.bankAccountInfo.source : undefined}
												paymentRequests={paymentRequests}
												updatePaymentRequests={updatePaymentRequests}
												loadingPaymentRequests={loadingPaymentRequests}
												onRecalibrate={onRecalibrateTransactions}
											/>
										),
									},
									...sftpTabs,
								]}
							/>
						) : (
							<Transactions
								bankAccountId={props.bankAccountId}
								bankAccountName={
									props.bankAccountInfo !== undefined
										? props.bankAccountInfo.account_description
										: localStorage.getItem('account_name')
								}
								bankAccountDescription={
									props.bankAccountInfo !== undefined
										? props.bankAccountInfo.bank_account_name
										: localStorage.getItem('account_name')
								}
								onLoadTransactions={onLoadTransactions}
								transactionsLoading={transactionsLoading}
								currentMonth={currentMonth}
								currentYear={currentYear}
								transactionsTable={transactionsTable}
								pendingTable={pendingTable}
								scheduledTable={scheduledTable}
								canceledTable={canceledTable}
								achDebitRequestsTable={achDebitRequestsTable}
								loadingTransactions={transactionsLoading}
								loadingIncompleteTransactions={loadingIncompleteTransactions}
								loadingAchDebitRequests={loadingAchDebitRequests}
								completeTransactionsNumerator={completedTransactions.length}
								completeTransactionsDenominator={totalRecordsTransactions}
								cancelAchDebitRequest={cancelAchDebitRequest}
								portfolioName={props.bankAccountInfo ? props.bankAccountInfo.subportfolio : undefined}
								propertyName={props.bankAccountInfo ? props.bankAccountInfo.property : undefined}
								apiSource={props.bankAccountInfo !== undefined ? props.bankAccountInfo.source : undefined}
								paymentRequests={paymentRequests}
								updatePaymentRequests={updatePaymentRequests}
								loadingPaymentRequests={loadingPaymentRequests}
								onRecalibrate={onRecalibrateTransactions}
							/>
						)}

					</Space>
				}
			/>

		</Space>
	);
};

export default OpenedBankAccount;